import { isArray, startCase } from 'lodash';
import { isEmpty } from '@jjj/components';
import { getCountryByName } from 'src/helpers/countriesHelpers';
import { isPostalCode } from '../../../helpers/countriesHelpers';
import actions from './actions';
import { createReducer } from '../../../helpers/reducer';
import { keyBy } from '../../../helpers/core';
import { t } from '../../../i18n';

const initialFormData = {
  name: '',
  address: '',
  zip: '',
  main_logo: null,
  icon: null,
  size: 'standard',
  sector: null,
  countries: [],
};

const initState = {
  data: {},
  editForm: null,
  loading: true,
  isRequestPending: false,
  baseErrors: [],
};

const colorsAttributes = ['primary_colors', 'secondary_colors'];

function validate(attr, value, isEditMode, state) {
  if (attr === 'name') {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', {
        fieldName: t('dash.organizations.organization.organization_name_label'),
      });
    }

    if (value.length < 3) {
      return t('dash.validation.min_length_error', {
        fieldName: t('dash.organizations.organization.organization_name_label'),
        length: 3,
      });
    }
  }

  if (attr === 'countries') {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', {
        fieldName: t('dash.organizations.organization.country_label'),
      });
    }
  }

  if (attr === 'zip') {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', {
        fieldName: t('dash.organizations.organization.zip_label'),
      });
    }

    const { countryCode, editForm } = state || {};
    const shortCode = countryCode?.toUpperCase();
    const selectedCountries = editForm?.countries?.value || [];

    const shortCodes = selectedCountries.map(c => getCountryByName(c.name)?.alpha2?.toUpperCase?.()).filter(Boolean);

    // Validate zip code
    const fieldName = t('dash.organizations.organization.zip_label');
    const invalidError = t('dash.validation.valid_error', { fieldName });

    if (shortCode) {
      if (!isPostalCode(value, shortCode)) return invalidError();
    } else if (shortCodes.length) {
      const isValid = shortCodes.some(code => isPostalCode(value, code));

      if (!isValid) return invalidError;
    }
  }

  if (!isEditMode) {
    if (attr === 'main_logo') {
      if (isEmpty(value)) {
        return t('dash.validation.required_error', {
          fieldName: t('dash.organizations.organization.main_logo_title'),
        });
      }
    }

    if (attr === 'icon') {
      if (isEmpty(value)) {
        return t('dash.validation.required_error', {
          fieldName: t('dash.organizations.organization.icon_title'),
        });
      }
    }
  }

  if (attr === 'address' && isEmpty(value)) {
    return t('dash.validation.required_error', {
      fieldName: t('dash.organizations.organization.address_label'),
    });
  }

  return '';
}

function onChangeProperty(state, { payload }) {
  const { attr, value, error } = payload;
  const { editForm, baseErrors } = state;

  editForm[attr] = {
    value,
    error: error || validate(attr, value, !!state.data?.id, state),
    name: attr,
    touched: true,
  };

  if (attr === 'size' && value === 'blank') {
    editForm.sector = { name: 'sector', value: null, error: '', touched: false };
  }

  const hasErrors = Object.values(editForm).some(({ errorMessage, touched }) => {
    return !!errorMessage && touched;
  });

  return {
    ...state,
    editForm: {
      ...editForm,
    },
    baseErrors: hasErrors ? baseErrors : [],
  };
}

function onClearStore() {
  return { ...initState };
}

function onCreate(state) {
  const propsArray = Object.keys(initialFormData);

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => {
      const valueToValidate = initialFormData[prop];

      return {
        name: prop,
        value: initialFormData[prop],
        error: validate(prop, valueToValidate, state.data?.id),
        touched: false,
      };
    },
  );

  return {
    ...state,
    loading: false,
    editForm: { ...editForm },
  };
}

function onEdit(state, { payload }) {
  const organization = { ...initialFormData, ...payload.data };
  const propsArray = Object.keys(organization);

  // prevent sending image urls on update
  const imagesFields = ['main_logo', 'icon'];

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => {
      const valueToValidate =
        colorsAttributes.includes(prop) && isArray(organization[prop]) ? organization[prop][0] : organization[prop];

      return {
        name: prop,
        value: imagesFields.includes(prop) ? null : organization[prop],
        error: validate(prop, valueToValidate, !!organization?.id),
        touched: false,
      };
    },
  );

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onLoadError(state) {
  return {
    ...state,
    loading: false,
  };
}

function onLoadRequest(state) {
  return {
    ...state,
    loading: true,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    loading: false,
    data,
  };
}

function onSaveOrganizationRequest(state) {
  return {
    ...state,
    isRequestPending: true,
  };
}

function onSaveOrganizationSuccess(state) {
  return {
    ...state,
    isRequestPending: false,
  };
}

function onError(state, { payload }) {
  const { editForm, baseErrors } = state;

  if (payload) {
    Object.keys(payload).forEach(key => {
      if (editForm[key]) {
        editForm[key].error = payload[key];
        editForm[key].touched = true;
      } else {
        const fieldName = startCase(key.replace('_', ' '));
        const errorMessages = payload[key];

        baseErrors.push(`${fieldName}: ${errorMessages.join(',')}`);
      }
    });
  }

  return {
    ...state,
    isRequestPending: false,
    editForm,
    baseErrors,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop], !!state.data?.id, state),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onLoadFormDataRequest(state) {
  return { ...state, loading: true };
}

function onLoadFormDataSuccess(state, { payload }) {
  const { formInfo } = payload;

  return { ...state, loading: false, formInfo };
}

export default createReducer(initState, {
  [actions.ADD_ERROR]: onError,
  [actions.ADD_REQUEST]: onSaveOrganizationRequest,
  [actions.ADD_SUCCESS]: onSaveOrganizationSuccess,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CREATE]: onCreate,
  [actions.EDIT]: onEdit,
  [actions.LOAD_ERROR]: onLoadError,
  [actions.LOAD_REQUEST]: onLoadRequest,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.LOAD_FORM_DATA_REQUEST]: onLoadFormDataRequest,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actions.SAVE_ERROR]: onError,
  [actions.SAVE_REQUEST]: onSaveOrganizationRequest,
  [actions.SAVE_SUCCESS]: onSaveOrganizationSuccess,
  [actions.TOUCH]: onTouch,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
});
